import { appConfig } from '@/utils/appConfig';
import { OpenApiSchemaParser } from '@/utils/openApiSchemaParser';
import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import { createApp, nextTick } from 'vue';
import App from './App.vue';
import router from './router';

import { $t } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';

import { SKIP_GLOBAL_ERROR_HANDLING_FLAG } from '@/composables/ErrorHandler';
import { initializeTheme } from '@/stores/themeStore';
import { setupGlobalErrorHandlers } from '@/utils/globalErrorHandlers';
import { i18n, initI18n, primeVueLocales, registerPrimeVueLocaleSetter } from '@/utils/i18n';

import '@/assets/styles.scss';

// Sentry Issue 100015811:
// Nach einem Deployment können bereits geöffnete Tabs auf entfernte JavaScript-Chunks zeigen.
// Vite stellt für diesen Fall ein eigenes Event bereit; ein Reload lädt die aktuelle index.html.
window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    window.location.reload();
});

initializeTheme();

const app = createApp(App);

const pinia = createPinia();

const beforeSendToSentry = (event, hint) => {
    // Derzeit betrifft dies nur den SlidingTimeout-Fehler
    // Sollten später noch weitere Fehler gefiltert werden, wäre ein eigenes SKIP_SENTRY_REPORTING sinnvoll.
    const skip = hint?.originalException?.[SKIP_GLOBAL_ERROR_HANDLING_FLAG];

    if (skip) {
        return null;
    }

    return event;
};

Sentry.init({
    app,
    dsn: appConfig.sentryDsn,
    environment: import.meta.env.MODE,
    beforeSend: beforeSendToSentry,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true
});

// Globale Error-Handler registrieren
setupGlobalErrorHandlers(app, router);

app.use(pinia);
app.use(i18n);
app.use(router);
app.use(PrimeVue, {
    locale: primeVueLocales[i18n.global.locale.value], // Sprache für die PrimeVue-Komponenten
    theme: {
        preset: Aura,
        options: {
            darkModeSelector: '.app-dark'
        }
    }
});
app.use(ToastService);
app.use(ConfirmationService);

// Registrierung des PrimeVue-Locale-Setters, damit i18n Sprachwechsel PrimeVue aktualisiert
registerPrimeVueLocaleSetter((sprache) => {
    app.config.globalProperties.$primevue.config.locale = primeVueLocales[sprache];
});

// Zuerst die Übersetzungen laden, dann die App mounten,
// damit nicht temporär irgendwelche Fallbacks angezeigt werden.
// Hinweis:
// Top-Level-await wird in unserem Build-Target (modules = default in vite) nicht unterstützt,
// deshalb verwenden wir IIFE ()= Immediately Invoked Function Expression)
(async () => {
    await initI18n();

    // OpenAPI Schema laden
    await OpenApiSchemaParser.loadSchema();

    await router.isReady();

    app.mount('#app');

    // 1 Render-Tick abwarten, dann den HTML-Loader entfernen
    await nextTick();

    document.getElementById('initial-loader')?.remove();
})();

// Apply the bav-blue theme immediately after mounting
const bavBluePresetExt = {
    semantic: {
        primary: {
            50: '#e6f0f9',
            100: '#cce1f3',
            200: '#99c3e7',
            300: '#66a5db',
            400: '#3387cf',
            500: '#005CB1', // Our custom blue color
            600: '#00539f',
            700: '#004a8d',
            800: '#00407b',
            900: '#003769',
            950: '#002e5a'
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{primary.500}',
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.600}',
                    activeColor: '{primary.700}'
                },
                highlight: {
                    background: '{primary.50}',
                    focusBackground: '{primary.100}',
                    color: '{primary.700}',
                    focusColor: '{primary.800}'
                }
            },
            dark: {
                primary: {
                    color: '{primary.400}',
                    contrastColor: '{surface.900}',
                    hoverColor: '{primary.300}',
                    activeColor: '{primary.200}'
                },
                highlight: {
                    background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                    focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
                    color: 'rgba(255,255,255,.87)',
                    focusColor: 'rgba(255,255,255,.87)'
                }
            }
        }
    }
};

$t().preset(Aura).preset(bavBluePresetExt).use({ useDefaultOptions: true });
